/**
 * RoleContext - Manages user role selection and persistence
 *
 * Handles:
 * - Role selection (therapist vs patient)
 * - localStorage persistence
 * - Cache clearing
 *
 * NOTE: PatientDisclaimerScreen has been COMPLETELY REMOVED
 * No hasSeenPatientDisclaimer flag - patients go directly to ClientView
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';

export type UserRole = 'therapist' | 'patient' | null;

interface RoleContextValue {
  role: UserRole;
  setRole: (role: UserRole) => void;
  hasAcceptedAgreements: boolean;
  markAgreementsAsAccepted: () => void;
  clearCache: () => void;
}

const RoleContext = createContext<RoleContextValue | undefined>(undefined);

interface RoleProviderProps {
  children: ReactNode;
}

export function RoleProvider({ children }: RoleProviderProps) {
  const storage = useLocalStorage();

  // Initialize state from localStorage
  const [role, setRoleState] = useState<UserRole>(() => {
    return storage.getItem('role') || null;
  });

  const [hasAcceptedAgreements, setHasAcceptedAgreements] = useState(() => {
    return storage.getItem('hasAcceptedAgreements') || false;
  });

  /**
   * Set role and persist to localStorage
   */
  const setRole = (newRole: UserRole) => {
    setRoleState(newRole);
    storage.setItem('role', newRole);

    if (newRole) {
      console.log(`[RoleContext] Role set to: ${newRole}`);
    }
  };

  /**
   * Mark legal agreements as accepted and persist
   */
  const markAgreementsAsAccepted = () => {
    setHasAcceptedAgreements(true);
    storage.setItem('hasAcceptedAgreements', true);
    console.log('[RoleContext] Legal agreements marked as accepted');
  };

  /**
   * Clear all cached data and reset app state
   */
  const clearCache = () => {
    console.log('[RoleContext] Clearing all cached data...');
    storage.clearAll();
    setRoleState(null);
    setHasAcceptedAgreements(false);
  };

  // Sync userId from auth if available (passive read, no active setting)
  useEffect(() => {
    const userId = storage.getItem('userId');
    if (userId) {
      console.log(`[RoleContext] User ID found: ${userId}`);
    }
  }, []);

  const value: RoleContextValue = {
    role,
    setRole,
    hasAcceptedAgreements,
    markAgreementsAsAccepted,
    clearCache,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

/**
 * Hook to access role context
 * Throws error if used outside RoleProvider
 */
export function useRole() {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
}
